// The platform pages' fixtures (chat, ontologies, connections, members, tokens, the record graph), for the home
// instance `michael` with `digiterium` connected. In memory, mutated by the mock api like a server would.
import type { Connection, Instance, Member, Message, Package, PackageDiff, Role, Snapshot, Space, Thread, Token } from "../types";
import { thread } from "./board";

const at = (min: number) => new Date(Date.UTC(2026, 9, 22, 9, 0) + min * 60_000).toISOString();

export const INSTANCES: Instance[] = [
	{ name: "michael", url: "https://hq.example.dev", home: true, live: true },
	{ name: "digiterium", url: "https://hq-digiterium.example.dev", home: false, live: true },
];

export type PlatformState = {
	spaces: Space[];
	threads: Thread[];
	messages: Message[];
	packages: Package[];
	connections: Connection[];
	members: Member[];
	roles: Role[];
	tokens: Token[];
};

const f = (name: string, type: string, required = false, many = false) => ({ name, type, required, many });
const r = (name: string, to: string, many = false, inverse: string | null = null) => ({ name, to, many, inverse });

const TRUSS: Package = {
	name: "truss",
	version: "1.0.0",
	description: "Base kinds every instance has: Entity, Person, Space, Member.",
	installed: "1.0.0",
	installedAt: at(-60 * 24 * 3),
	base: true,
	hooks: [],
	kinds: [
		{ name: "truss.Person", description: "Someone who acts: a human or an agent.", fields: [f("name", "string", true), f("kind", "enum(human, agent)", true)], relations: [], count: 5 },
		{ name: "truss.Space", description: "Scopes collaboration inside an instance.", fields: [f("name", "string", true)], relations: [], count: 3 },
		{ name: "truss.Member", description: "A person's roles in a space.", fields: [f("status", "enum(active, removed)", true)], relations: [r("person", "truss.Person"), r("space", "truss.Space"), r("roles", "truss.Role", true)], count: 6 },
	],
};
const TASKS: Package = {
	name: "hq.tasks",
	version: "1.0.0",
	description: "Tasks, projects and repos (the tasks prototype's schema).",
	installed: "1.0.0",
	installedAt: at(-60 * 24 * 2),
	base: true,
	hooks: ["on Task.status → notify assignee"],
	kinds: [
		{
			name: "hq.tasks.Task",
			description: "A unit of work with a lifecycle.",
			fields: [f("title", "string", true), f("body", "text"), f("status", "enum(todo, doing, review, done, dropped)", true), f("rank", "string")],
			relations: [r("project", "hq.tasks.Project", false, "tasks"), r("assignee", "truss.Person", false, "assigned"), r("parent", "hq.tasks.Task", false, "children"), r("blockedBy", "hq.tasks.Task", true, "blocks"), r("context", "truss.Entity", true, "contextOf")],
			count: 101,
		},
		{ name: "hq.tasks.Project", description: null, fields: [f("name", "string", true), f("description", "text"), f("status", "enum(active, paused, archived)", true)], relations: [r("repos", "hq.tasks.Repo", true, "projects")], count: 4 },
		{ name: "hq.tasks.Repo", description: null, fields: [f("path", "string", true), f("gitHosting", "enum(github, gitlab, other)", true)], relations: [], count: 0 },
	],
};
const CHAT: Package = {
	name: "hq.chat",
	version: "1.0.0",
	description: "Threads about any address, messages, reactions, private read marks.",
	installed: "1.0.0",
	installedAt: at(-60 * 24),
	base: true,
	hooks: ["on Message.create → notify thread followers (live)"],
	kinds: [
		{ name: "hq.chat.Thread", description: "A conversation about any address.", fields: [f("title", "string", true)], relations: [r("about", "truss.Entity", false, "threads"), r("space", "truss.Space", false, "threads")], count: 6 },
		{ name: "hq.chat.Message", description: null, fields: [f("body", "text", true)], relations: [r("thread", "hq.chat.Thread", false, "messages"), r("replyTo", "hq.chat.Message")], count: 14 },
		{ name: "hq.chat.Reaction", description: null, fields: [f("emoji", "string", true)], relations: [r("message", "hq.chat.Message", false, "reactions")], count: 3 },
		{ name: "hq.chat.ReadMark", description: "Private: only its owner reads it.", fields: [], relations: [r("thread", "hq.chat.Thread"), r("message", "hq.chat.Message")], count: 4 },
	],
};
const DESIGN: Package = {
	name: "hq.design",
	version: "1.0.0",
	description: "Plan, Step, Decision, Question, Requirement.",
	installed: null,
	base: false,
	hooks: [],
	kinds: [
		{ name: "hq.design.Plan", description: null, fields: [f("title", "string", true), f("status", "enum(draft, approved, done)", true)], relations: [r("steps", "hq.design.Step", true, "plan")], count: null },
		{ name: "hq.design.Step", description: null, fields: [f("title", "string", true), f("status", "enum(todo, doing, done)", true)], relations: [], count: null },
		{ name: "hq.design.Decision", description: null, fields: [f("title", "string", true), f("status", "enum(proposed, accepted, superseded)", true), f("body", "text")], relations: [r("buildsOn", "hq.design.Decision", true)], count: null },
		{ name: "hq.design.Question", description: null, fields: [f("title", "string", true), f("status", "enum(open, answered)", true)], relations: [], count: null },
		{ name: "hq.design.Requirement", description: null, fields: [f("title", "string", true)], relations: [], count: null },
	],
	installedAt: null,
};
const INFRA: Package = {
	name: "hq.infra",
	version: "1.0.0",
	description: "Port, Hosting, Deployment, Method: where things run.",
	installed: null,
	base: false,
	hooks: ["cron daily → check deployments"],
	kinds: [
		{ name: "hq.infra.Deployment", description: null, fields: [f("name", "string", true), f("url", "string")], relations: [r("hosting", "hq.infra.Hosting")], count: null },
		{ name: "hq.infra.Hosting", description: null, fields: [f("name", "string", true)], relations: [], count: null },
	],
	installedAt: null,
};

export function platformFixture(s: Snapshot): PlatformState {
	const me = { id: "usr_michael", name: "Michael" };
	const n = { id: "usr_n", name: "N" };
	const b = { id: "usr_b", name: "B" };
	// a project is a truss.Space (hq.tasks extends it): the chat spaces are the board's projects
	const spaces: Space[] = s.projects.map((x) => ({ id: x.id, name: x.name, instance: x.instance, members: x.instance ? 4 : 2 }));
	const task = (i: number) => s.tasks[i]!;
	const t1 = task(0); // Web UI: booklet layout (michael)
	const t2 = task(10); // Landing copy v2 (digiterium)
	const threads: Thread[] = ([
		{ id: "thr_general", spaceId: "spc_tasks", title: "General", about: "spc_tasks", aboutKind: "Space", lastAt: at(42), lastText: null, unread: 0, instance: null },
		{ id: "digiterium/thr_eclion", spaceId: "digiterium/spc_eclion", title: "General", about: "digiterium/spc_eclion", aboutKind: "Space", lastAt: at(55), lastText: null, unread: 2, instance: "digiterium" },
		{ id: "digiterium/thr_osmix", spaceId: "digiterium/spc_osmix", title: "General", about: "digiterium/spc_osmix", aboutKind: "Space", lastAt: at(12), lastText: null, unread: 0, instance: "digiterium" },
		{ id: "thr_task1", spaceId: "spc_tasks", title: t1.title, about: t1.id, aboutKind: "Task", lastAt: at(30), lastText: null, unread: 0, instance: null },
		{ id: "digiterium/thr_task2", spaceId: "digiterium/spc_eclion", title: t2.title, about: t2.id, aboutKind: "Task", lastAt: at(50), lastText: null, unread: 1, instance: "digiterium" },
	] as Parameters<typeof thread>[0][]).map(thread);
	let k = 0;
	const msg = (threadId: string, by: { id: string; name: string }, body: string, min: number, reactions: Message["reactions"] = []): Message => ({
		id: `${threadId.includes("/") ? `${threadId.split("/")[0]}/` : ""}msg_${(k++).toString().padStart(4, "0")}`,
		threadId,
		by,
		body,
		at: at(min),
		reactions,
		pending: false,
	});
	const messages: Message[] = [
		msg("thr_general", me, "Board and chat run on the kit now.", 0),
		msg("thr_general", { id: "usr_agentcurator", name: "agent:curator" }, "I'll post a summary of every lane here when it reports.", 5),
		msg("thr_general", me, "👍 board first, then chats.", 42, [{ emoji: "👍", count: 1, mine: false }]),
		msg("digiterium/thr_eclion", n, "Client sent the new brand guide, it's in the task about landing copy.", 20),
		msg("digiterium/thr_eclion", me, "Great, no more Telegram hand-offs: discuss right here.", 31, [{ emoji: "🔥", count: 2, mine: true }]),
		msg("digiterium/thr_eclion", b, "Deploy preview is up, review when you can.", 55),
		msg("digiterium/thr_osmix", b, "Osmix kickoff moved to Friday.", 12),
		msg("thr_task1", me, "Booklet ported; next: chats in the task sheet.", 25),
		msg("thr_task1", { id: "usr_agenttasksweb", name: "agent:tasks-web" }, "On it. The sheet gets a Thread section under Context.", 30),
		msg("digiterium/thr_task2", n, "Draft v2 attached, headline still open.", 44),
		msg("digiterium/thr_task2", me, "Headline: \"Your graph, your instance\"?", 50),
	];
	for (const t of threads) t.lastText = messages.filter((m) => m.threadId === t.id).at(-1)?.body ?? null;
	const roles: Role[] = [
		{ id: "rol_owner", name: "owner", permissions: ["*"] },
		{ id: "rol_member", name: "member", permissions: ["*.read", "hq.tasks.Task.*", "hq.chat.*"] },
		{ id: "rol_viewer", name: "viewer", permissions: ["*.read"] },
	];
	const person = (id: string) => s.persons.find((p) => p.id === id) ?? { id, name: id, kind: "human" as const };
	const members: Member[] = [
		{ id: "mem_michael", person: person("usr_michael"), space: null, roles: ["owner"], status: "active" },
		{ id: "mem_curator", person: person("usr_agentcurator"), space: { id: "spc_tasks", name: "Tasks" }, roles: ["member"], status: "active" },
		{ id: "mem_tasksweb", person: person("usr_agenttasksweb"), space: { id: "spc_tasks", name: "Tasks" }, roles: ["member"], status: "active" },
		{ id: "mem_b", person: person("usr_b"), space: { id: "spc_tasks", name: "Tasks" }, roles: ["viewer"], status: "active" },
	];
	const tokens: Token[] = [
		{ id: "pat_laptop", name: "laptop-cli", createdAt: at(-60 * 24 * 5), lastUsedAt: at(40), expiresAt: null, revoked: false },
		{ id: "pat_curator", name: "agent:curator", createdAt: at(-60 * 24 * 2), lastUsedAt: at(5), expiresAt: null, revoked: false },
		{ id: "pat_old", name: "old-phone", createdAt: at(-60 * 24 * 30), lastUsedAt: at(-60 * 24 * 20), expiresAt: null, revoked: true },
	];
	const connections: Connection[] = [
		{ id: "cnx_digiterium", direction: "outgoing", instance: "digiterium", url: INSTANCES[1]!.url, person: "Michael", scopes: ["read", "write"], createdAt: at(-60 * 24), lastUsedAt: at(55), status: "active" },
		{ id: "cnx_bdemo_in", direction: "incoming", instance: "b-demo", url: "https://hq-b-demo.example.dev", person: "B", scopes: ["read"], createdAt: at(-60 * 3), lastUsedAt: null, status: "active" },
	];
	return { spaces, threads, messages, packages: [TRUSS, TASKS, CHAT, DESIGN, INFRA].map((p) => structuredClone(p)), connections, members, roles, tokens };
}

/** Install/upgrade preview: everything a not-installed package brings is an addition. */
export function diffOf(p: Package, version: string): PackageDiff {
	return {
		name: p.name,
		from: p.installed,
		to: version,
		added: p.installed
			? []
			: p.kinds.flatMap((k): PackageDiff["added"] => [
					{ what: "kind" as const, name: k.name, detail: k.description },
					...k.fields.map((x) => ({ what: "field" as const, name: `${k.name}.${x.name}`, detail: x.type })),
					...k.relations.map((x) => ({ what: "relation" as const, name: `${k.name}.${x.name}`, detail: `→ ${x.to}${x.many ? " (many)" : ""}` })),
				]).concat(p.hooks.map((h): PackageDiff["added"][number] => ({ what: "hook", name: h, detail: null }))),
		refused: [],
		hooks: p.hooks,
	};
}

